import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, ExternalLink, Instagram, RefreshCw, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import {
  beginInstagramConnect,
  clearPendingInstagramConnection,
  getPendingInstagramConnection,
  INSTAGRAM_OAUTH_CHANNEL,
  INSTAGRAM_OAUTH_COMPLETED_MESSAGE,
  INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY,
  INSTAGRAM_OAUTH_STARTED_EVENT,
  normalizeInstagramReturnPath,
} from '../../utils/instagramConnect';
import './InstagramOAuthRecovery.scss';

const PROFILE_VERIFICATION_ATTEMPTS = 4;
const PROFILE_VERIFICATION_DELAY_MS = 750;

const waitForProfileRetry = (attempt) => new Promise(resolve => {
  window.setTimeout(resolve, PROFILE_VERIFICATION_DELAY_MS * attempt);
});

const getInstagramProfile = (user) => (
  user?.user_type === 'business' ? user?.business_profile : user?.creator_profile
);

const clearStoredCompletionIfMatching = (completion) => {
  const storedCompletion = localStorage.getItem(INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY);
  if (!storedCompletion) return;
  try {
    const stored = JSON.parse(storedCompletion);
    if (stored.attemptId === completion?.attemptId && stored.createdAt === completion?.createdAt) {
      localStorage.removeItem(INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY);
    }
  } catch {
    localStorage.removeItem(INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY);
  }
};

const InstagramOAuthRecovery = () => {
  const { t } = useTranslation('common');
  const { currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState(() => getPendingInstagramConnection());
  const [connected, setConnected] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const completionHandledRef = useRef(false);
  const completionToVerifyRef = useRef(null);
  const launchResetRef = useRef(null);
  const locationRef = useRef(location);
  const currentUserRef = useRef(currentUser);
  const pendingRef = useRef(pending);
  const initialCompletionCheckedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    locationRef.current = location;
    currentUserRef.current = currentUser;
    pendingRef.current = pending;
  }, [currentUser, location, pending]);

  const handleCompletion = useCallback(async (completion) => {
    if (completion?.type !== INSTAGRAM_OAUTH_COMPLETED_MESSAGE) return;
    const completionAge = Date.now() - Number(completion.createdAt || 0);
    if (completionAge < 0 || completionAge > 5 * 60 * 1000) {
      clearStoredCompletionIfMatching(completion);
      return;
    }
    const activePending = getPendingInstagramConnection() || pendingRef.current;
    if (activePending?.attemptId && completion.attemptId !== activePending.attemptId) {
      clearStoredCompletionIfMatching(completion);
      return;
    }
    if (completionHandledRef.current) return;
    completionHandledRef.current = true;
    completionToVerifyRef.current = completion;

    window.clearTimeout(launchResetRef.current);
    const completionReturnTo = normalizeInstagramReturnPath(
      completion.returnTo || activePending?.returnTo || '/complete-profile'
    );
    setVerifying(true);
    setVerificationFailed(false);

    let verifiedUser = null;
    for (let attempt = 1; attempt <= PROFILE_VERIFICATION_ATTEMPTS; attempt += 1) {
      let refreshedUser = null;
      try {
        refreshedUser = await refreshUser?.();
      } catch (error) {
        console.error('Error refreshing user after Instagram authorization:', error);
      }
      if (!mountedRef.current || completionToVerifyRef.current !== completion) return;

      const candidateUser = refreshedUser || currentUserRef.current;
      if (getInstagramProfile(candidateUser)?.instagram_account_connected) {
        verifiedUser = candidateUser;
        break;
      }
      if (attempt < PROFILE_VERIFICATION_ATTEMPTS) {
        await waitForProfileRetry(attempt);
      }
      if (!mountedRef.current || completionToVerifyRef.current !== completion) return;
    }

    if (completionToVerifyRef.current !== completion) return;

    if (!verifiedUser) {
      completionHandledRef.current = false;
      setLaunching(false);
      setConnected(false);
      setVerifying(false);
      setVerificationFailed(true);
      const nextPending = getPendingInstagramConnection() || activePending;
      pendingRef.current = nextPending;
      setPending(nextPending);
      return;
    }

    localStorage.removeItem(INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY);
    clearPendingInstagramConnection();
    pendingRef.current = null;
    completionToVerifyRef.current = null;
    setLaunching(false);
    setVerifying(false);
    setVerificationFailed(false);
    setConnected(true);
    toast.success(
      <div>
        <strong>{t('instagram_connection.toast.success_title')}</strong>
        <div>{t('instagram_connection.toast.success_body')}</div>
      </div>,
      {
        autoClose: 10000,
        closeOnClick: false,
        toastId: 'instagram-connected-to-foth',
      }
    );

    const currentPath = `${locationRef.current.pathname}${locationRef.current.search || ''}`;
    const sourcePath = activePending?.sourcePath;
    if ((!sourcePath || sourcePath === currentPath) && completionReturnTo !== currentPath) {
      navigate(completionReturnTo, { replace: true });
    }
  }, [navigate, refreshUser, t]);

  useEffect(() => {
    const onStarted = (event) => {
      const nextPending = event.detail || getPendingInstagramConnection();
      completionHandledRef.current = false;
      completionToVerifyRef.current = null;
      pendingRef.current = nextPending;
      setConnected(false);
      setVerifying(false);
      setVerificationFailed(false);
      setPending(nextPending);
    };
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      handleCompletion(event.data);
    };
    const onStorage = (event) => {
      if (event.key !== INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY || !event.newValue) return;
      try {
        handleCompletion(JSON.parse(event.newValue));
      } catch {
        localStorage.removeItem(INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY);
      }
    };

    window.addEventListener(INSTAGRAM_OAUTH_STARTED_EVENT, onStarted);
    window.addEventListener('message', onMessage);
    window.addEventListener('storage', onStorage);

    let channel;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(INSTAGRAM_OAUTH_CHANNEL);
      channel.onmessage = (event) => handleCompletion(event.data);
    }

    return () => {
      window.removeEventListener(INSTAGRAM_OAUTH_STARTED_EVENT, onStarted);
      window.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
      channel?.close();
    };
  }, [handleCompletion]);

  useEffect(() => {
    if (initialCompletionCheckedRef.current) return;
    initialCompletionCheckedRef.current = true;
    const storedCompletion = localStorage.getItem(INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY);
    if (!storedCompletion) return;
    try {
      handleCompletion(JSON.parse(storedCompletion));
    } catch {
      localStorage.removeItem(INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY);
    }
  }, [handleCompletion]);

  useEffect(() => {
    if (!connected) return undefined;
    const timer = window.setTimeout(() => {
      setConnected(false);
      setPending(null);
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [connected]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      window.clearTimeout(launchResetRef.current);
    };
  }, []);

  if (!pending && !connected) return null;

  const dismiss = () => {
    completionHandledRef.current = false;
    completionToVerifyRef.current = null;
    pendingRef.current = null;
    clearPendingInstagramConnection();
    localStorage.removeItem(INSTAGRAM_OAUTH_COMPLETION_STORAGE_KEY);
    setPending(null);
    setConnected(false);
    setVerifying(false);
    setVerificationFailed(false);
  };

  const continueAuthorization = () => {
    if (launching || verifying || !pending) return;
    setLaunching(true);
    try {
      beginInstagramConnect(pending.returnTo);
    } catch (error) {
      console.error('Error reopening Instagram authorization:', error);
      setLaunching(false);
      return;
    }
    window.clearTimeout(launchResetRef.current);
    launchResetRef.current = window.setTimeout(() => setLaunching(false), 3000);
  };

  const retryVerification = () => {
    if (verifying || !completionToVerifyRef.current) return;
    handleCompletion(completionToVerifyRef.current);
  };

  return (
    <aside
      className={`instagram-oauth-recovery ${connected ? 'is-connected' : ''}`}
      role="region"
      aria-live="polite"
      aria-label={t('instagram_connection.recovery.status_label')}
    >
      <button
        className="instagram-oauth-recovery__close"
        type="button"
        onClick={dismiss}
        aria-label={t('instagram_connection.recovery.dismiss')}
      >
        <X size={18} />
      </button>

      <div className="instagram-oauth-recovery__icon">
        {connected
          ? <CheckCircle2 size={24} />
          : verificationFailed
            ? <RefreshCw size={24} />
            : <Instagram size={24} />}
      </div>

      <div className="instagram-oauth-recovery__content">
        <span className="instagram-oauth-recovery__eyebrow">
          {t('instagram_connection.recovery.eyebrow')}
        </span>
        <strong>
          {connected
            ? t('instagram_connection.recovery.success_title')
            : verificationFailed
              ? t('instagram_connection.recovery.verification_failed_title')
              : t('instagram_connection.recovery.pending_title')}
        </strong>
        <p>
          {connected
            ? t('instagram_connection.recovery.success_body')
            : verificationFailed
              ? t('instagram_connection.recovery.verification_failed_body')
              : t('instagram_connection.recovery.pending_body')}
        </p>

        {!connected && pending && (verificationFailed ? (
          <button type="button" onClick={retryVerification} disabled={verifying}>
            <RefreshCw className={verifying ? 'spin' : ''} size={16} />
            {verifying
              ? t('instagram_connection.recovery.verifying')
              : t('instagram_connection.recovery.check_again')}
          </button>
        ) : (
          <button type="button" onClick={continueAuthorization} disabled={launching || verifying}>
            <ExternalLink size={16} />
            {verifying
              ? t('instagram_connection.recovery.verifying')
              : launching
                ? t('instagram_connection.recovery.opening')
                : t('instagram_connection.recovery.continue')}
          </button>
        ))}
      </div>
    </aside>
  );
};

export default InstagramOAuthRecovery;
